import React, { useEffect, useState } from 'react';
import { Popover } from '@mantine/core';

import { showToast } from 'utils/utility';
import { getRole } from 'utils/routeUtility';
import { useApi, useFlightData } from 'libs/data-access';
import { IFlightOnlineBookingList } from 'libs/data-access/types';
import { EButton, ECheckbox, EPlaceholder, EPopover, ESegmentedControl, ETextArea, ETextInput } from 'libs/ui';

interface IProps {
    label: string;
    flightTicket: IFlightOnlineBookingList;
    handleRefresh: () => void;
};

export default function MarkAsToBeRefunded({ ...props }: IProps) {

    const {
        getData
    } = useApi();
    const {
        postFlightData
    } = useFlightData();

    const [journeySegments, setJourneySegments] = useState<{
        label: string; value: string; disabled: boolean;
    }[]>([]);
    const [cancellationCharges, setCancellationCharges] = useState<{
        affiliateDomesticCharge: number;
        affiliateInternationalCharge: number;
        customerDomesticCharge: number;
        customerInternationalCharge: number;
    }>({
        affiliateDomesticCharge: 0,
        affiliateInternationalCharge: 0,
        customerDomesticCharge: 0,
        customerInternationalCharge: 0
    });
    const [rejectTicketState, setRejectTicketState] = useState<{
        opened: boolean;
        selectedBooking: { id: number; key: string; };
        supplierCharge: string | null;
        cancellationCharge: string | null;
        remark: string | null;
        confirmed: boolean;
        isSaving: boolean;
    }>({
        opened: false,
        selectedBooking: { id: 0, key: "" },
        supplierCharge: null,
        cancellationCharge: null,
        remark: null,
        confirmed: false,
        isSaving: false
    });


    useEffect(() => {
        if (!props.flightTicket.uuid) return;

        getCancellationCharges();
        createJourneySegments();
    }, [props.flightTicket]);


    // HANDLER FUNCTIONS
    const closePopover = () => setRejectTicketState({
        opened: false,
        selectedBooking: { id: 0, key: "" },
        supplierCharge: null,
        cancellationCharge: null,
        remark: null,
        confirmed: false,
        isSaving: false
    });

    const createJourneySegments = () => {
        let segments: { label: string; value: string; disabled: boolean }[] = [];

        props.flightTicket.bookingResponses.forEach(
            booking => {
                // for galileo journeys
                booking.galileoBookingDetailResponses?.forEach(
                    galJourney => segments.push({
                        label: `${galJourney.origin} - ${galJourney.destination}`,
                        value: `${booking.id}/${galJourney.origin}_${galJourney.destination}`,
                        disabled:
                            galJourney.bookingStatus !== "FAILED" &&
                            galJourney.bookingStatus !== "CANCELLED" &&
                            galJourney.bookingStatus !== "TICKETING_PENDING"
                    })
                );

                // for navitaire
                booking.navitaireBookingDetailResponses?.forEach(
                    navJourney => segments.push({
                        label: `${navJourney.origin} - ${navJourney.destination}`,
                        value: `${booking.id}/${navJourney.origin}_${navJourney.destination}`,
                        disabled:
                            navJourney.bookingStatus !== "FAILED" &&
                            navJourney.bookingStatus !== "CANCELLED" &&
                            navJourney.bookingStatus !== "TICKETING_PENDING"
                    })
                );

                // for akbar
                booking.akbarBookingDetailResponses?.forEach(
                    abkarJourney => segments.push({
                        label: `${abkarJourney.origin} - ${abkarJourney.destination}`,
                        value: `${booking.id}/${abkarJourney.origin}_${abkarJourney.destination}`,
                        disabled:
                            abkarJourney.bookingStatus !== "FAILED" &&
                            abkarJourney.bookingStatus !== "CANCELLED" &&
                            abkarJourney.bookingStatus !== "TICKETING_PENDING" &&
                            abkarJourney.bookingStatus !== "PENDING"
                    })
                );
            }
        );

        // console.log("segments: ", segments);

        setJourneySegments(segments);
    };


    // API CALLS
    const getCancellationCharges = async () => {
        const response = await getData(`my-charges/flights/cancellation-charges?userType=${getRole()?.[0]}`);
        if (response.isSuccess) {
            console.log("Response while fetching cancellation charges: ", response.data.data);
            setCancellationCharges(response.data.data);
            setRejectTicketState({
                ...rejectTicketState,
                cancellationCharge: response.data.data.customerDomesticCharge.toString()
            });
        } else {
            console.log("Error while fetching cancellation charges: ", response);
            if (response.status !== 404) {
                showToast({
                    type: "error",
                    message: response.message || "Error while fetching cancellation charges",
                });
            };
        };
    };

    const resolveTicket = async () => {

        if (!rejectTicketState.selectedBooking) {
            showToast({
                time: 5 * 1000,
                type: "warning",
                message: "Please select a booking and provide the ticket information first!"
            });
            return;
        };

        if (!rejectTicketState.supplierCharge || !rejectTicketState.cancellationCharge) {
            showToast({
                type: "warning",
                message: "Please fill the charges!"
            });
            return;
        };

        if (!rejectTicketState.remark) {
            showToast({
                type: "warning",
                message: "Please enter remarks!"
            });
            return;
        };

        // console.log("rejectTicketState: ", rejectTicketState);

        setRejectTicketState({ ...rejectTicketState, isSaving: true });

        const response = await postFlightData(`/booking/${rejectTicketState.selectedBooking.id}/cancellation-charges?key=${rejectTicketState.selectedBooking.key}&supplierCharge=${rejectTicketState.supplierCharge}&cancellationCharge=${rejectTicketState.cancellationCharge}&remark=${rejectTicketState.remark}`, null);

        if (response.isSuccess) {
            console.log("Response for resolving ticket: ", response);
            showToast({
                type: "success",
                message: "Ticket rejected successfully!"
            });
            closePopover();
            props.handleRefresh();
        } else {
            console.log("Error while resolving ticket: ", response);
            showToast({
                type: "error",
                message: response.message || "Error while resolving ticket!"
            });
        };

        setRejectTicketState({ ...rejectTicketState, isSaving: false });

    };


    return (
        <EPopover
            width={450}
            opened={rejectTicketState.opened}
            onClose={closePopover}
        >
            <Popover.Target>
                <div>
                    <EButton
                        size="sm"
                        variant="light"
                        color="red"
                        onClick={() => setRejectTicketState({ ...rejectTicketState, opened: !rejectTicketState.opened })}
                    >{props.label}</EButton>
                </div>
            </Popover.Target>

            <Popover.Dropdown>
                <div className="w-96 p-2">
                    <h3 className="text-lg font-medium mb-4">Mark as To Be Refunded</h3>
                    <div className="flex flex-col gap-6">
                        <ESegmentedControl
                            fullWidth
                            data={journeySegments}
                            value={`${rejectTicketState.selectedBooking.id}/${rejectTicketState.selectedBooking.key}`}
                            onChange={(value) => setRejectTicketState({
                                ...rejectTicketState,
                                selectedBooking: {
                                    id: Number(value.split("/")[0]),
                                    key: value.split("/")[1]
                                }
                            })}
                        />

                        {
                            rejectTicketState.selectedBooking.id && rejectTicketState.selectedBooking.key
                                ?
                                <>
                                    <ETextInput
                                        label="Supplier Charge"
                                        placeholder="Enter supplier charge"
                                        value={rejectTicketState.supplierCharge ?? undefined}
                                        onChange={(e) => setRejectTicketState({ ...rejectTicketState, supplierCharge: e.target.value })}
                                    />
                                    <ETextInput
                                        label="My Cancellation Charge"
                                        placeholder="Enter my cancellation charge"
                                        value={rejectTicketState.cancellationCharge ?? undefined}
                                        onChange={(e) => setRejectTicketState({ ...rejectTicketState, cancellationCharge: e.target.value })}
                                    />

                                    <ETextArea
                                        label="Remarks"
                                        placeholder="Please enter remarks"
                                        value={rejectTicketState.remark ?? undefined}
                                        onChange={e => setRejectTicketState({ ...rejectTicketState, remark: e.target.value })}
                                    />

                                    <ECheckbox
                                        // indeterminate
                                        label="Are you sure you want to resolve this ticket?"
                                        checked={rejectTicketState.confirmed}
                                        onChange={(e) => setRejectTicketState({ ...rejectTicketState, confirmed: e.target.checked })}
                                    />

                                    {
                                        rejectTicketState.confirmed
                                            ?
                                            <div className="flex justify-end gap-2">
                                                <EButton
                                                    variant="light"
                                                    onClick={closePopover}
                                                >Cancel</EButton>
                                                <EButton
                                                    onClick={resolveTicket}
                                                >Save</EButton>
                                            </div>
                                            :
                                            <></>
                                    }
                                </>
                                :
                                <EPlaceholder
                                    title="Please select a journey from above."
                                />
                        }
                    </div>
                </div>
            </Popover.Dropdown>
        </EPopover>
    )
}